'use client'

import { useState, useEffect, useMemo, useCallback } from 'react'
import { useLocale } from 'next-intl'
import { Link, useRouter } from '@/i18n/navigation'
import { t } from '@/i18n/translations'
import { fetchCryptoPrices, type CryptoPrice } from '@/services/crypto'
import { useSignalR } from '@/hooks/useSignalR'
import type { PriceUpdate } from '@/services/signalrService'
import BrokerRecommendations from '@/components/BrokerRecommendations'
import { cryptoBrokersAll } from '@/data/indexData'

interface CryptoRow {
  symbol: string
  name: string
  imageUrl: string
  price: number
  change24h: number
  marketCap: number
  volume24h: number
}

type SortKey = 'symbol' | 'price' | 'change24h' | 'marketCap' | 'volume24h'
type SortDir = 'asc' | 'desc'

/* Format Helpers */

function fmtPrice(val: number): string {
  if (val >= 1) return '$' + val.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return '$' + val.toPrecision(4)
}

function fmtVol(vol: number): string {
  if (vol >= 1_000_000_000_000) return '$' + (vol / 1_000_000_000_000).toFixed(2) + 'T'
  if (vol >= 1_000_000_000) return '$' + (vol / 1_000_000_000).toFixed(2) + 'B'
  if (vol >= 1_000_000) return '$' + (vol / 1_000_000).toFixed(2) + 'M'
  if (vol >= 1_000) return '$' + (vol / 1_000).toFixed(1) + 'K'
  return '$' + vol.toLocaleString('en-US')
}

function fmtPct(pct: number): string {
  return '%' + Math.abs(pct).toFixed(2).replace('.', ',')
}

function Skeleton({ w, h = 16, className = '' }: { w: number; h?: number; className?: string }) {
  return (
    <div
      className={`animate-pulse rounded ${className}`}
      style={{ width: w, height: h, backgroundColor: 'var(--border-color)' }}
    />
  )
}

function CoinIcon({ symbol, imageUrl, up }: { symbol: string; imageUrl: string; up: boolean }) {
  const [imgFailed, setImgFailed] = useState(false)
  if (imageUrl && !imgFailed) {
    return (
      <img
        src={imageUrl}
        alt={symbol}
        className="w-8 h-8 rounded-full object-cover shrink-0"
        onError={() => setImgFailed(true)}
      />
    )
  }
  return (
    <div
      className="w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-bold text-white shrink-0"
      style={{ backgroundColor: up ? 'var(--chart-up)' : 'var(--chart-down)' }}
    >
      {symbol[0]}
    </div>
  )
}

export default function CryptoListClient() {
  const lang = useLocale()
  const router = useRouter()

  const [prices, setPrices] = useState<Record<string, CryptoPrice>>({})
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('marketCap')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const loadPrices = useCallback(async () => {
    try {
      const data = await fetchCryptoPrices([])
      setPrices(data)
    } catch { /* ignore */ }
    setLoading(false)
  }, [])

  useEffect(() => {
    loadPrices()
    const iv = setInterval(loadPrices, 60_000)
    return () => clearInterval(iv)
  }, [loadPrices])

  const handlePriceUpdate = useCallback((update: PriceUpdate) => {
    setPrices(prev => {
      // Backend sends BTCUSD, state keys are BTC (CoinGecko format, no USD suffix)
      const sym = update.symbol.endsWith('USD') ? update.symbol.slice(0, -3) : update.symbol
      if (!prev[sym]) return prev
      if (prev[sym].usd === update.price) return prev
      return { ...prev, [sym]: { ...prev[sym], usd: update.price } }
    })
  }, [])

  const { isLive } = useSignalR({ onPriceUpdate: handlePriceUpdate })

  // Build rows from all returned prices
  const rows: CryptoRow[] = useMemo(() =>
    Object.entries(prices)
      .map(([sym, p]) => ({
        symbol: sym,
        name: p.name || sym,
        imageUrl: p.imageUrl || '',
        price: p.usd || 0,
        change24h: p.usd_24h_change || 0,
        marketCap: p.marketCap || 0,
        volume24h: p.volume24h || 0,
      }))
      .filter(r => r.price > 0)
  , [prices])

  // Search filter
  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return rows
    const q = searchQuery.toLowerCase()
    return rows.filter(r =>
      r.symbol.toLowerCase().includes(q) || r.name.toLowerCase().includes(q)
    )
  }, [rows, searchQuery])

  // Sort
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const av = a[sortKey] as number | string
      const bv = b[sortKey] as number | string
      if (typeof av === 'string') return sortDir === 'asc' ? (av as string).localeCompare(bv as string) : (bv as string).localeCompare(av as string)
      return sortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number)
    })
  }, [filtered, sortKey, sortDir])

  function handleSort(key: SortKey) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(key); setSortDir('desc') }
  }

  function sortIcon(key: SortKey) {
    if (sortKey !== key) return ' \u2195'
    return sortDir === 'asc' ? ' \u2191' : ' \u2193'
  }

  // Summary cards
  const topGainer = useMemo(() =>
    rows.length > 0 ? rows.reduce((a, b) => a.change24h > b.change24h ? a : b) : null
  , [rows])
  const topLoser = useMemo(() =>
    rows.length > 0 ? rows.reduce((a, b) => a.change24h < b.change24h ? a : b) : null
  , [rows])
  const largestCap = useMemo(() =>
    rows.length > 0 ? rows.reduce((a, b) => a.marketCap > b.marketCap ? a : b) : null
  , [rows])

  const navigateToDetail = (sym: string) => {
    // Küçük harf kanonik biçimdir (backend sitemap'i de öyle yayıyor); GlobalMarkets
    // zaten küçük harf link veriyordu, buradaki ham sembol ikinci bir URL üretiyordu.
    router.push({ pathname: '/crypto/[symbol]', params: { symbol: sym.toLowerCase() } })
  }

  return (
    <main className="max-w-7xl mx-auto py-5 px-4 sm:px-6 lg:px-4">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-[13px] mb-6 overflow-hidden whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
        <Link href="/" className="hover:underline transition-colors shrink-0" style={{ color: 'var(--accent-color)' }}>{t('nav.home', lang)}</Link>
        <span className="select-none shrink-0">/</span>
        <span className="font-semibold truncate" style={{ color: 'var(--text-main)' }}>{t('crypto.title', lang)}</span>
      </nav>

      {/* Heading */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-extrabold tracking-tight m-0" style={{ color: 'var(--text-main)' }}>
            {t('crypto.title', lang)}
          </h1>
          {isLive && (
            <span className="flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(34,197,94,0.12)', color: 'var(--chart-up)' }}>
              <span className="w-1.5 h-1.5 rounded-full animate-pulse inline-block" style={{ backgroundColor: 'var(--chart-up)' }} />
              {lang === 'en' ? 'Live' : 'Canlı'}
            </span>
          )}
        </div>
        <p className="text-sm mt-1 m-0" style={{ color: 'var(--text-muted)' }}>
          {t('crypto.desc', lang)}
        </p>
      </div>

      {/* Summary Cards */}
      {!loading && rows.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {/* Top Gainer */}
          <div
            className="rounded-xl border p-4 cursor-pointer hover:opacity-90 transition-opacity"
            style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
            onClick={() => topGainer && navigateToDetail(topGainer.symbol)}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--chart-up)' }} />
              <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
                {t('crypto.topGainer', lang)}
              </span>
            </div>
            {topGainer && (
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[15px] font-bold" style={{ color: 'var(--text-main)' }}>{topGainer.symbol}</div>
                  <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{topGainer.name}</div>
                </div>
                <span className="text-[14px] font-semibold tabular-nums" style={{ color: 'var(--chart-up)' }}>
                  +{fmtPct(topGainer.change24h)}
                </span>
              </div>
            )}
          </div>

          {/* Top Loser */}
          <div
            className="rounded-xl border p-4 cursor-pointer hover:opacity-90 transition-opacity"
            style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
            onClick={() => topLoser && navigateToDetail(topLoser.symbol)}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--chart-down)' }} />
              <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
                {t('crypto.topLoser', lang)}
              </span>
            </div>
            {topLoser && (
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[15px] font-bold" style={{ color: 'var(--text-main)' }}>{topLoser.symbol}</div>
                  <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{topLoser.name}</div>
                </div>
                <span className="text-[14px] font-semibold tabular-nums" style={{ color: 'var(--chart-down)' }}>
                  -{fmtPct(topLoser.change24h)}
                </span>
              </div>
            )}
          </div>

          {/* Largest Market Cap */}
          <div
            className="rounded-xl border p-4 cursor-pointer hover:opacity-90 transition-opacity"
            style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
            onClick={() => largestCap && navigateToDetail(largestCap.symbol)}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--accent-color)' }} />
              <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
                {t('crypto.largestCap', lang)}
              </span>
            </div>
            {largestCap && (
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[15px] font-bold" style={{ color: 'var(--text-main)' }}>{largestCap.symbol}</div>
                  <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{largestCap.name}</div>
                </div>
                <span className="text-[14px] font-semibold tabular-nums" style={{ color: 'var(--text-main)' }}>
                  {fmtVol(largestCap.marketCap)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="flex items-center mb-4">
        <div className="relative sm:ml-auto w-full sm:w-auto">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
            width="16" height="16" viewBox="0 0 24 24"
            fill="none" stroke="var(--accent-color)" strokeWidth="2.2" strokeLinecap="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder={t('crypto.searchPlaceholder', lang)}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="constituent-search pl-9 pr-3 py-2 rounded-lg text-[13px] outline-none w-full sm:w-72 transition-shadow"
            style={{
              backgroundColor: 'var(--bg-card)',
              color: 'var(--text-main)',
              border: '1.5px solid var(--accent-color)',
              boxShadow: '0 0 0 3px color-mix(in srgb, var(--accent-color) 14%, transparent)',
            }}
          />
        </div>
      </div>

      {/* Desktop Table */}
      <div
        className="rounded-xl border overflow-hidden hidden sm:block"
        style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse" style={{ minWidth: 700 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                {([
                  { key: 'symbol' as SortKey, label: t('crypto.coin', lang), align: 'text-left', px: 'px-6' },
                  { key: 'price' as SortKey, label: t('crypto.price', lang), align: 'text-right', px: 'px-3' },
                  { key: 'change24h' as SortKey, label: t('crypto.24hChange', lang), align: 'text-right', px: 'px-3' },
                  { key: 'marketCap' as SortKey, label: t('crypto.marketCap', lang), align: 'text-right', px: 'px-3' },
                  { key: 'volume24h' as SortKey, label: t('crypto.volume', lang), align: 'text-right', px: 'px-6' },
                ] as const).map(col => (
                  <th scope="col"
                    key={col.key}
                    className={`py-3 text-[11px] font-bold uppercase tracking-widest cursor-pointer select-none ${col.px} ${col.align}`}
                    style={{ color: 'var(--text-muted)' }}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}{sortIcon(col.key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 20 }).map((_, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td className="px-6 py-3.5"><Skeleton w={120} /></td>
                      <td className="px-3 py-3.5 text-right"><Skeleton w={80} className="ml-auto" /></td>
                      <td className="px-3 py-3.5 text-right"><Skeleton w={60} className="ml-auto" /></td>
                      <td className="px-3 py-3.5 text-right"><Skeleton w={80} className="ml-auto" /></td>
                      <td className="px-6 py-3.5 text-right"><Skeleton w={80} className="ml-auto" /></td>
                    </tr>
                  ))
                : sorted.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-12 text-[13px] font-medium" style={{ color: 'var(--text-muted)' }}>
                        {searchQuery ? t('status.noResults', lang) : t('status.loadingData', lang)}
                      </td>
                    </tr>
                  )
                : sorted.map((r, i) => {
                    const up = r.change24h >= 0
                    const c = up ? 'var(--chart-up)' : 'var(--chart-down)'
                    return (
                      <tr
                        key={r.symbol}
                        className="cursor-pointer transition-colors duration-100 hover:bg-[var(--bg-body)]"
                        style={{ borderBottom: i < sorted.length - 1 ? '1px solid var(--border-color)' : 'none' }}
                        onClick={() => navigateToDetail(r.symbol)}
                      >
                        <td className="px-6 py-3.5">
                          <div className="flex items-center gap-3">
                            <CoinIcon symbol={r.symbol} imageUrl={r.imageUrl} up={up} />
                            <div>
                              <div className="text-[13px] font-bold leading-tight" style={{ color: 'var(--text-main)' }}>{r.symbol}</div>
                              <div className="text-[10px] mt-0.5 leading-tight" style={{ color: 'var(--text-muted)' }}>{r.name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3.5 text-right">
                          <span className="text-[13px] font-semibold tabular-nums" style={{ color: 'var(--text-main)' }}>{fmtPrice(r.price)}</span>
                        </td>
                        <td className="px-3 py-3.5 text-right">
                          <span
                            className="inline-block px-2 py-0.5 rounded-md text-[12px] font-semibold tabular-nums"
                            style={{
                              backgroundColor: up ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                              color: c,
                            }}
                          >
                            {up ? '+' : '-'}{fmtPct(r.change24h)}
                          </span>
                        </td>
                        <td className="px-3 py-3.5 text-right">
                          <span className="text-[13px] tabular-nums" style={{ color: 'var(--text-muted)' }}>{fmtVol(r.marketCap)}</span>
                        </td>
                        <td className="px-6 py-3.5 text-right">
                          <span className="text-[13px] tabular-nums" style={{ color: 'var(--text-muted)' }}>{fmtVol(r.volume24h)}</span>
                        </td>
                      </tr>
                    )
                  })}
            </tbody>
          </table>
        </div>
        {!loading && sorted.length > 0 && (
          <div className="px-6 py-3 border-t text-[11px]" style={{ borderColor: 'var(--border-color)', color: 'var(--text-muted)' }}>
            {sorted.length} {t('crypto.listed', lang)}
          </div>
        )}
      </div>

      {/* Mobile Cards */}
      <div className="sm:hidden flex flex-col gap-3">
        {loading
          ? Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="rounded-xl border p-4 animate-pulse" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                <Skeleton w={120} h={20} />
                <div className="mt-2 flex justify-between">
                  <Skeleton w={80} />
                  <Skeleton w={60} />
                </div>
              </div>
            ))
          : sorted.map(r => {
              const up = r.change24h >= 0
              const c = up ? 'var(--chart-up)' : 'var(--chart-down)'
              return (
                <div
                  key={r.symbol}
                  className="rounded-xl border p-4 cursor-pointer active:opacity-80 transition-opacity"
                  style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
                  onClick={() => navigateToDetail(r.symbol)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2.5">
                      <CoinIcon symbol={r.symbol} imageUrl={r.imageUrl} up={up} />
                      <div>
                        <div className="text-[14px] font-bold" style={{ color: 'var(--text-main)' }}>{r.symbol}</div>
                        <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{r.name}</div>
                      </div>
                    </div>
                    <span
                      className="inline-block px-2 py-0.5 rounded-md text-[12px] font-semibold tabular-nums"
                      style={{
                        backgroundColor: up ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                        color: c,
                      }}
                    >
                      {up ? '+' : '-'}{fmtPct(r.change24h)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[15px] font-semibold tabular-nums" style={{ color: 'var(--text-main)' }}>{fmtPrice(r.price)}</span>
                    <span className="text-[11px] tabular-nums" style={{ color: 'var(--text-muted)' }}>
                      {t('crypto.marketCap', lang)}: {fmtVol(r.marketCap)}
                    </span>
                  </div>
                </div>
              )
            })}
      </div>

      {/* Kripto borsaları — Türkiye + Global tek bölümde, 8 broker */}
      <div className="mt-6">
        <BrokerRecommendations
          brokers={cryptoBrokersAll}
          source="crypto_list"
          customHeadline={lang === 'en'
            ? '💸 Ready to buy crypto?'
            : '💸 Kripto Almaya Hazır mısın?'}
          customSubline={lang === 'en'
            ? 'Compare Turkey-based (TRY deposit) and global exchanges. Open an account in minutes — zero & low commission options available.'
            : 'Türkiye merkezli (TL para yatırma) ve global borsaları karşılaştır. Dakikalar içinde hesap aç — sıfır ve düşük komisyon seçenekleri mevcut.'}
          customCta={lang === 'en' ? 'Open a crypto account →' : 'Kripto Hesabı Aç →'}
        />
      </div>
    </main>
  )
}
